import { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Divider,
} from '@mui/material';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import { TranslationContext } from '../Context/TranslationContext';
import { useTranslation } from '../hooks/useTranslation';
import { LOCALE_BY_LANG } from '../Utils/i18nHelpers';
import { getQuotes, regenerateQuoteLink } from '../Utils/quotesApi';

function formatDate(dateStr, locale) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(locale);
}

function formatMontant(amountCents, currency, locale) {
    if (amountCents == null) return '—';
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: (currency || 'eur').toUpperCase(),
    }).format(amountCents / 100);
}

export default function QuotesPage() {
    const t = useTranslation('Quotes.json');
    const { currentLanguage } = useContext(TranslationContext);
    const locale = LOCALE_BY_LANG[currentLanguage] || LOCALE_BY_LANG.fr;
    const OUTCOME_CONFIG = {
        pending: { label: t('quotes.outcome.pending'), color: 'warning' },
        accepted: { label: t('quotes.outcome.accepted'), color: 'success' },
        declined: { label: t('quotes.outcome.declined'), color: 'error' },
    };
    const INTERVAL_LABEL = {
        day: t('quotes.interval.day'),
        week: t('quotes.interval.week'),
        month: t('quotes.interval.month'),
        year: t('quotes.interval.year'),
    };
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [redirectingId, setRedirectingId] = useState(null);

    useEffect(() => {
        getQuotes()
            .then(({ quotes: data }) => setQuotes(data))
            .catch(() => setError(t('quotes.error')))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRowClick = (quote) => {
        if (quote.outcome !== 'pending') {
            setSelectedQuote(quote);
            return;
        }
        setRedirectingId(quote.id);
        setError(null);
        regenerateQuoteLink(quote.id)
            .then(({ ephemeral_url }) => {
                window.location.href = ephemeral_url;
            })
            .catch(() => {
                setError(t('quotes.error'));
                setRedirectingId(null);
            });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <RequestQuoteOutlinedIcon sx={{ color: '#FF7233', fontSize: 32 }} />
                <Typography variant="h5" fontWeight={600}>
                    {t('quotes.title')}
                </Typography>
            </Box>

            {loading && (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress sx={{ color: '#FF7233' }} />
                </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && quotes.length === 0 && (
                <Alert severity="info">{t('quotes.empty')}</Alert>
            )}

            {!loading && !error && quotes.some((q) => q.outcome === 'pending') && (
                <Alert severity="info" sx={{ mb: 2 }}>{t('quotes.pendingNotice')}</Alert>
            )}

            {!loading && !error && quotes.length > 0 && (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><Typography fontWeight={600}>{t('quotes.column.title')}</Typography></TableCell>
                                <TableCell><Typography fontWeight={600}>{t('quotes.column.type')}</Typography></TableCell>
                                <TableCell><Typography fontWeight={600}>{t('quotes.column.outcome')}</Typography></TableCell>
                                <TableCell><Typography fontWeight={600}>{t('quotes.column.amount')}</Typography></TableCell>
                                <TableCell><Typography fontWeight={600}>{t('quotes.column.date')}</Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {quotes.map((quote) => {
                                const outcome = OUTCOME_CONFIG[quote.outcome] || { label: quote.outcome, color: 'default' };
                                return (
                                    <TableRow
                                        key={quote.id}
                                        hover
                                        onClick={() => handleRowClick(quote)}
                                        sx={{ cursor: redirectingId ? 'default' : 'pointer' }}
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2">{quote.title}</Typography>
                                                {redirectingId === quote.id && <CircularProgress size={14} />}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {quote.type === 'subscription' ? t('quotes.type.subscription') : t('quotes.type.one_time')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={outcome.label} color={outcome.color} size="small" />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatMontant(quote.amount, quote.currency, locale)}
                                                {quote.type === 'subscription' ? ` ${INTERVAL_LABEL[quote.interval] || ''}` : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(quote.decided_at || quote.created_at, locale)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={!!selectedQuote} onClose={() => setSelectedQuote(null)} maxWidth="sm" fullWidth>
                {selectedQuote && (
                    <>
                        <DialogTitle>{selectedQuote.title}</DialogTitle>
                        <DialogContent>
                            <Stack spacing={1.5}>
                                {selectedQuote.description && (
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                        {selectedQuote.description}
                                    </Typography>
                                )}
                                {selectedQuote.items?.length > 1 && (
                                    <>
                                        <Divider />
                                        {selectedQuote.items.map((item, index) => (
                                            <Stack key={index} spacing={0.25}>
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography variant="body2">{item.title}</Typography>
                                                    <Typography variant="body2">
                                                        {formatMontant(item.amount, selectedQuote.currency, locale)}
                                                    </Typography>
                                                </Stack>
                                                {item.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.description}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        ))}
                                    </>
                                )}
                                <Divider />
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">{t('quotes.column.amount')}</Typography>
                                    <Typography variant="body2">
                                        {formatMontant(selectedQuote.amount, selectedQuote.currency, locale)}
                                        {selectedQuote.type === 'subscription' ? ` ${INTERVAL_LABEL[selectedQuote.interval] || ''}` : ''}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">{t('quotes.column.type')}</Typography>
                                    <Typography variant="body2">
                                        {selectedQuote.type === 'subscription' ? t('quotes.type.subscription') : t('quotes.type.one_time')}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">{t('quotes.column.outcome')}</Typography>
                                    <Chip
                                        label={(OUTCOME_CONFIG[selectedQuote.outcome] || { label: selectedQuote.outcome }).label}
                                        color={(OUTCOME_CONFIG[selectedQuote.outcome] || { color: 'default' }).color}
                                        size="small"
                                    />
                                </Stack>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">{t('quotes.column.date')}</Typography>
                                    <Typography variant="body2">
                                        {formatDate(selectedQuote.decided_at || selectedQuote.created_at, locale)}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setSelectedQuote(null)}>{t('quotes.close')}</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
}
